import { useAuth } from "@mindlercare/auth-ui";
import { useFlag } from "@mindlercare/unleash-client";
import { Typography } from "components/typography";
import CONSTANTS from "core/constants";
import type { Slot } from "core/types";
import { isBefore, addHours, fromUnixTime, sub } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mindlercare/ui-web";
import { getMeetingUrl } from "./getMeetingUrl";

interface SlotMeetingLinkProps {
  slot: Slot;
}

export const SlotMeetingLink: React.FC<SlotMeetingLinkProps> = ({ slot }) => {
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const startsAt = sub(fromUnixTime(slot.startsAt), {
    minutes: CONSTANTS.CAN_CONNECT_BEFORE,
  });
  const endsAt = sub(fromUnixTime(slot.endsAt), {
    minutes: CONSTANTS.CAN_CONNECT_BEFORE,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const startsMs = sub(fromUnixTime(slot.startsAt), {
      minutes: CONSTANTS.CAN_CONNECT_BEFORE,
    }).getTime();
    const delay = startsMs - now.getTime();
    if (delay <= 0) return;
    const id = setTimeout(() => setNow(new Date()), delay);
    return () => clearTimeout(id);
  }, [now, slot.startsAt]);
  const vonageVideoFilterEnabled = useFlag("VONAGE_VIDEO_FILTER");

  const meetingUrl = getMeetingUrl({
    slotId: slot.slotId,
    vonageVideoFilterEnabled,
  });

  const shouldShowMeetingLink =
    slot.payed !== 0 &&
    slot.status === CONSTANTS.BOOKING_STATUS.BOOKED &&
    !isBefore(now, startsAt) &&
    isBefore(now, addHours(endsAt, 1));

  const onMeetingClick = async () => {
    setIsLoading(true);
    await refresh();

    setIsLoading(false);
    window.open(meetingUrl, CONSTANTS.MEETING_WINDOW_NAME.VIDEO);
  };

  return (
    <>
      {shouldShowMeetingLink ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Button
            onClick={onMeetingClick}
            variant={"primary"}
            disabled={isLoading}
            testId="cy-join-meeting-button"
          >
            {t("JOIN_MEETING")}
          </Button>
        </div>
      ) : (
        <>
          <Typography insensitive variant="h3" sx={{ marginBottom: "10px" }}>
            {t("START_MEETING")}
          </Typography>
          <Typography insensitive color="GRAY_DARK" variant="body-small">
            {t("DISPLAY_MEETING_LINK")}
          </Typography>
        </>
      )}
    </>
  );
};
